import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { migrationStatusText } from "@/lib/historicalMigration";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// R13 Workstream 5 - GOVERNED historical migration. A draft can always be saved and always lists
// every missing prerequisite; an operational Case is created only when the backend says the record
// is complete AND a leader has attested. The backend owns every rule - this form only renders it.
const SOURCES = [
  ["legacy_crm", "Legacy CRM"], ["spreadsheet", "Spreadsheet"], ["paper_files", "Paper files"],
  ["email_archive", "Email archive"], ["other", "Other"],
];
const lines = (t) => (t || "").split("\n").map((x) => x.trim()).filter(Boolean);
const blank = () => ({
  migration_id: null, contact: null, product_code: "", is_test_record: null,
  historical_opportunity_ref: "", legacy_case_ref: "", starting_stage: "",
  consultation_basis: "", consultation_legacy_note: "", consultation_evidence: "",
  signup_date: "", signup_evidence: "", payment_status: "", payment_evidence: "", payment_exception_reason: "",
  sales_owner_user_id: "", process_owner_user_id: "", process_team: "",
  migration_source: "", migration_reason: "",
});

export const HistoricalMigrationDialog = ({ open, setOpen, onDone }) => {
  const { hasCap, hasExplicitCap } = useAuth();
  const [f, setF] = useState(blank());
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [workflows, setWorkflows] = useState({});
  const [users, setUsers] = useState([]);
  const [rec, setRec] = useState(null);       // last server view: status + missing_prerequisites
  const [busy, setBusy] = useState(false);
  const [statement, setStatement] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const canTest = hasCap("admin.config");
  const canAttest = hasCap("approvals.manage");
  // Activation needs the explicit migration grant (an administrator wildcard does not count) AND approval authority.
  const canActivate = hasExplicitCap("case.exception.migration") && canAttest;

  useEffect(() => {
    if (!open) { setF(blank()); setRec(null); setQ(""); setResults([]); setStatement(""); return; }
    api.get("/cases/workflows").then((r) => setWorkflows(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data.users || r.data)).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);

  const payload = useMemo(() => ({
    migration_id: f.migration_id, contact_id: f.contact?.contact_id, product_code: f.product_code,
    is_test_record: f.is_test_record,
    historical_opportunity_ref: f.historical_opportunity_ref, legacy_case_ref: f.legacy_case_ref,
    starting_stage: f.starting_stage || null, consultation_basis: f.consultation_basis || null,
    consultation_legacy_note: f.consultation_legacy_note, consultation_evidence_refs: lines(f.consultation_evidence),
    signup_date: f.signup_date || null, signup_evidence_refs: lines(f.signup_evidence),
    payment_status: f.payment_status || null, payment_evidence_refs: lines(f.payment_evidence),
    payment_exception_reason: f.payment_exception_reason,
    sales_owner_user_id: f.sales_owner_user_id || null, process_owner_user_id: f.process_owner_user_id || null,
    process_team: f.process_team, migration_source: f.migration_source || null,
    migration_reason: f.migration_reason,
  }), [f]);

  const saveDraft = async () => {
    if (!f.contact || !f.product_code) { toast.warning("Pick the customer and the specific product first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/migration/cases", payload);
      setRec(data); set("migration_id", data.migration_id);
      toast[data.ready_to_activate ? "success" : "info"](
        data.ready_to_activate ? "Complete - ready for activation" : `Draft saved - ${data.missing_prerequisites.length} prerequisite(s) still missing`);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const attest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/migration/cases/${rec.migration_id}/attest`, { statement });
      setRec(data); toast.success("Attested");
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/migration/cases/${rec.migration_id}/activate`);
      toast.success(`Case ${data.case_id} created from the historical record`);
      setOpen(false); onDone && onDone(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.missing_prerequisites) setRec((p) => ({ ...p, missing_prerequisites: d.missing_prerequisites, ready_to_activate: false }));
      toast.error(errDetail(e), { duration: 9000 });
    } finally { setBusy(false); }
  };

  const missing = rec?.missing_prerequisites || [];
  const stages = workflows[f.product_code] || [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="migration-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            Governed historical migration <Badge variant="outline" className="text-[10px]">Historical record</Badge>
          </DialogTitle>
          <DialogDescription>
            For customers who were onboarded before SalesOS. Nothing here is a new sale: no conversion credit, Meta event,
            signup approval or consultation outcome is created. A Case is created only when every prerequisite below is
            present and a leader has attested.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2 text-sm">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Customer</Label>
            {f.contact ? (
              <p>{f.contact.full_name} <span className="font-mono text-xs text-muted-foreground">{f.contact.customer_id}</span>{" "}
                <button className="text-xs underline" onClick={() => set("contact", null)}>change</button></p>
            ) : (
              <>
                <Input data-testid="mig-customer-search" placeholder="Name / phone / AP-…" value={q} onChange={(e) => setQ(e.target.value)} />
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {results.map((r) => (
                    <button key={r.contact_id} className="w-full text-left p-1.5 rounded border text-sm hover:bg-muted/50"
                      onClick={() => { set("contact", r); set("is_test_record", !!r.is_test_record); }}>
                      {r.full_name} <span className="font-mono text-xs text-muted-foreground">{r.customer_id}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Specific product</Label>
            <Select value={f.product_code} onValueChange={(v) => { set("product_code", v); set("starting_stage", ""); }}>
              <SelectTrigger data-testid="mig-product" className="h-9"><SelectValue placeholder="Pick product" /></SelectTrigger>
              <SelectContent>{Object.keys(workflows).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Stage the Case had reached</Label>
            <Select value={f.starting_stage} onValueChange={(v) => set("starting_stage", v)}>
              <SelectTrigger data-testid="mig-stage" className="h-9"><SelectValue placeholder="Pick stage" /></SelectTrigger>
              <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Historical opportunity / pursuit reference</Label>
            <Input data-testid="mig-opp-ref" value={f.historical_opportunity_ref} onChange={(e) => set("historical_opportunity_ref", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Legacy Case / file reference</Label>
            <Input data-testid="mig-case-ref" value={f.legacy_case_ref} onChange={(e) => set("legacy_case_ref", e.target.value)} /></div>

          <div className="space-y-1.5">
            <Label className="text-xs">Consultation outcome</Label>
            <Select value={f.consultation_basis} onValueChange={(v) => set("consultation_basis", v)}>
              <SelectTrigger data-testid="mig-consult-basis" className="h-9"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="signup">Signup (governed outcome)</SelectItem>
                <SelectItem value="legacy_equivalent">Legacy equivalent (describe)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Signup date</Label>
            <Input type="date" data-testid="mig-signup-date" value={f.signup_date} onChange={(e) => set("signup_date", e.target.value)} /></div>
          {f.consultation_basis === "legacy_equivalent" && (
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Legacy equivalent of the consultation outcome</Label>
              <Textarea rows={2} value={f.consultation_legacy_note} onChange={(e) => set("consultation_legacy_note", e.target.value)} /></div>
          )}
          <div className="space-y-1.5"><Label className="text-xs">Consultation evidence (one reference per line)</Label>
            <Textarea rows={2} data-testid="mig-consult-evidence" value={f.consultation_evidence} onChange={(e) => set("consultation_evidence", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Signup evidence (one reference per line)</Label>
            <Textarea rows={2} data-testid="mig-signup-evidence" value={f.signup_evidence} onChange={(e) => set("signup_evidence", e.target.value)} /></div>

          <div className="space-y-1.5">
            <Label className="text-xs">Payment</Label>
            <Select value={f.payment_status} onValueChange={(v) => set("payment_status", v)}>
              <SelectTrigger data-testid="mig-payment" className="h-9"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="verified">Verified (evidence attached)</SelectItem>
                <SelectItem value="exception">Documented leadership exception</SelectItem>
                <SelectItem value="pending">Pending / unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.payment_status === "verified" && (
            <div className="space-y-1.5"><Label className="text-xs">Payment evidence (one reference per line)</Label>
              <Textarea rows={2} value={f.payment_evidence} onChange={(e) => set("payment_evidence", e.target.value)} /></div>
          )}
          {f.payment_status === "exception" && (
            <div className="space-y-1.5"><Label className="text-xs">Exception reason</Label>
              <Textarea rows={2} value={f.payment_exception_reason} onChange={(e) => set("payment_exception_reason", e.target.value)} /></div>
          )}

          <div className="space-y-1.5"><Label className="text-xs">Sales owner</Label>
            <Select value={f.sales_owner_user_id} onValueChange={(v) => set("sales_owner_user_id", v)}>
              <SelectTrigger data-testid="mig-sales-owner" className="h-9"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs">Process owner (or name a team)</Label>
            <Select value={f.process_owner_user_id} onValueChange={(v) => set("process_owner_user_id", v)}>
              <SelectTrigger data-testid="mig-process-owner" className="h-9"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="…or process team" value={f.process_team} onChange={(e) => set("process_team", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Migration source</Label>
            <Select value={f.migration_source} onValueChange={(v) => set("migration_source", v)}>
              <SelectTrigger data-testid="mig-source" className="h-9"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>{SOURCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs">Why is this being migrated?</Label>
            <Textarea rows={2} data-testid="mig-reason" value={f.migration_reason} onChange={(e) => set("migration_reason", e.target.value)} /></div>

          <div className="space-y-1.5 sm:col-span-2 rounded border p-2.5" data-testid="mig-test-classification">
            <Label className="text-xs">TEST classification (explicit - never inferred from a name, phone or email)</Label>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={f.is_test_record === false ? "default" : "outline"}
                onClick={() => set("is_test_record", false)}>Real customer record</Button>
              <Button type="button" size="sm" variant={f.is_test_record === true ? "default" : "outline"}
                disabled={!canTest} title={canTest ? "" : "Only an authorised administrator can classify TEST data"}
                data-testid="mig-test-yes" onClick={() => set("is_test_record", true)}>TEST data</Button>
            </div>
          </div>
        </div>

        {rec && (
          <div data-testid="mig-missing-panel" role="status"
            className={`rounded border p-2.5 text-xs space-y-1 ${missing.length ? "border-amber-500/50 bg-amber-500/10" : "border-emerald-500/50 bg-emerald-500/10"}`}>
            <p className="font-medium">
              {migrationStatusText(rec)}
            </p>
            {missing.length > 0 && (
              <ul className="list-disc pl-5">{missing.map((m) => <li key={m.code} data-testid={`mig-missing-${m.code}`}>{m.message}</li>)}</ul>
            )}
            {rec.leadership_attestation?.by && (
              <p className="text-muted-foreground">Attested by {rec.leadership_attestation.by_name || rec.leadership_attestation.by}: “{rec.leadership_attestation.statement}”</p>
            )}
          </div>
        )}
        {rec && canAttest && !rec.leadership_attestation?.by && (
          <div className="space-y-1.5">
            <Label className="text-xs">Leadership attestation - what did you check?</Label>
            <Textarea rows={2} data-testid="mig-attest-statement" value={statement} onChange={(e) => setStatement(e.target.value)} />
            <Button type="button" size="sm" variant="outline" data-testid="mig-attest" disabled={busy || statement.trim().length < 10} onClick={attest}>
              Attest evidence has been checked
            </Button>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="mig-save-draft" disabled={busy} onClick={saveDraft}>Save draft</Button>
          <Button data-testid="mig-activate" disabled={busy || !rec?.ready_to_activate || !canActivate} onClick={activate}
            title={!canActivate ? "Needs the explicit case.exception.migration grant and approval authority"
              : (rec?.ready_to_activate ? "" : "Disabled until every prerequisite is present and attested")}>
            Create Case from historical record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
